import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import { Collections } from "@/types/pocketbase-types";
import { useAuth } from "@/context/auth-context";
import Feedback from "@/components/application/feedback/Feedback";
import type { UserScoreResponse } from "@/types/pocketbase-types";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useSnackbar } from "notistack";
import { useEffect, useRef } from "react";

const BookQuizResultsPage = () => {
  const { slug } = useParams({ from: "/books/quiz-results/$slug" });
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const hasShownToast = useRef(false);

  // Fetch user score for this book
  const {
    data: userScore,
    isLoading: isLoadingScore,
    error,
  } = useQuery({
    queryKey: ["book-quiz-score", slug, user?.id],
    queryFn: async (): Promise<UserScoreResponse | null> => {
      if (!user?.id || !slug) return null;

      try {
        const { id: bookId } = await pb
          .collection(Collections.Books)
          .getFirstListItem(`slug="${slug}" && phase="processed"`);

        const record = (await pb
          .collection(Collections.UserScore)
          .getFirstListItem(
            `user="${user.id}" && book="${bookId}"`
          )) as UserScoreResponse;
        return record;
      } catch (error: any) {
        if (error?.status === 404) return null;
        throw error;
      }
    },
    enabled: !!user?.id && !!slug,
  });

  // Show points gained toast when score is loaded
  useEffect(() => {
    if (userScore && userScore.score !== undefined && !hasShownToast.current) {
      hasShownToast.current = true;
      enqueueSnackbar(`🎉 You earned ${userScore.score} points!`, {
        variant: "success",
        autoHideDuration: 4000,
      });
    }
  }, [userScore, enqueueSnackbar]);

  if (!slug || isLoadingScore) {
    return (
      <LoadingSpinner text="Loading your results..." className="min-h-screen" />
    );
  }

  if (error || !userScore) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-error-500 text-6xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-error-500 mb-2">
            Error Loading Results
          </h2>
          <p className="text-neutral-500">
            {error
              ? "Failed to load your quiz results."
              : "No quiz results found for this book."}
          </p>
        </div>
      </div>
    );
  }

  // Calculate display values using new fields
  const displayScore = userScore.correct_answers || 0;
  const totalQuestions = userScore.total_questions || 10;
  const percentage =
    totalQuestions > 0 ? (displayScore / totalQuestions) * 100 : 0;
  const stars = Math.round((percentage / 100) * 3);

  return (
    <Feedback
      score={displayScore}
      totalQuestions={totalQuestions}
      starsToFill={stars}
      slug={slug}
    />
  );
};

export default BookQuizResultsPage;
